/**
 * Documents spinner behavior for ERP Accelerator.
 */

import * as React from "react"
import { LoaderCircle } from "lucide-react"

import { cn } from "@/lib/utils"

const sizeClasses = {
  sm: "h-4 w-4",
  md: "h-6 w-6",
  lg: "h-10 w-10",
} as const

/**
 * Describes spinner props data.
 */
export interface SpinnerProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: keyof typeof sizeClasses
  label?: string
}

/**
 * Handles spinner behavior.
 */
function Spinner({ size = "md", label, className, ...props }: SpinnerProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn("flex items-center justify-center gap-2", className)}
      {...props}
    >
      <LoaderCircle
        className={cn("animate-spin text-primary", sizeClasses[size])}
        aria-hidden="true"
      />
      {label ? (
        <span className="text-sm text-muted-foreground">{label}</span>
      ) : (
        <span className="sr-only">Loading</span>
      )}
    </div>
  )
}

/**
 * Describes full page spinner props data.
 */
export interface FullPageSpinnerProps {
  label?: string
  className?: string
}

/**
 * Handles full page spinner behavior.
 */
function FullPageSpinner({ label, className }: FullPageSpinnerProps) {
  return (
    <div
      className={cn(
        "flex min-h-[60vh] w-full items-center justify-center animate-fadeIn",
        className
      )}
    >
      <div className="flex flex-col items-center gap-3">
        <LoaderCircle
          className="h-10 w-10 animate-spin text-primary"
          aria-hidden="true"
        />
        <p className="text-sm text-muted-foreground">{label ?? "Loading..."}</p>
      </div>
    </div>
  )
}

export { Spinner, FullPageSpinner }
